import DOMPurify from 'dompurify';

/**
 * Sanitizes stored HTML before it is assigned to the editor root.
 * Assigning innerHTML executes markup like <img onerror=...> immediately,
 * so a malicious payload saved through the API would otherwise run in the
 * admin/instructor session the moment an Edit form opens.
 *
 * The profile mirrors what the editor produces (plus legacy iframes so
 * old TinyMCE-era video embeds are not dropped from re-saved content);
 * scripts, event handlers and javascript: URIs are removed. The student
 * portal is protected separately (server-side sanitization on serve).
 */
export function sanitizeForSeed(html: string): string {
	if (!html) return html;
	return DOMPurify.sanitize(html, {
		USE_PROFILES: { html: true, mathMl: true, svg: true },
		ADD_TAGS: ['iframe'],
		ADD_ATTR: [
			'data-value',
			'data-list',
			'target',
			'allow',
			'allowfullscreen',
			'frameborder',
		],
	});
}

/**
 * STUDENT-authored text (essay / assignment text answers) rendered to a
 * grader. The student backend stores every text answer as sanitized HTML
 * (normalised on write, legacy rows converted once), and the backends serve
 * it as stored — so this is the ONLY sanitizer between the row and the
 * grader's DOM. The student toolbar can only produce inline formatting,
 * lists, alignment and direction; everything else — links, images, iframes,
 * svg/MathML, inline styles — is stripped. Nothing is sniffed: "a<b then
 * c>d" arrives already escaped.
 */
export function sanitizeStudentHtml(html: string | null | undefined): string {
	if (!html) return '';
	// No USE_PROFILES here: a profile widens the allowlist back to the full
	// HTML set and silently drops the ALLOWED_* options below. No
	// ALLOWED_URI_REGEXP either: nothing in this allowlist carries a URI, and
	// DOMPurify matches every non-URI-safe attribute value (dir="rtl") against it.
	return DOMPurify.sanitize(html, {
		ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'sub', 'sup', 'ol', 'ul', 'li', 'span', 'blockquote', 'pre', 'code'],
		ALLOWED_ATTR: ['class', 'dir', 'data-list'],
		FORBID_ATTR: ['style'],
	});
}
